import { useEffect } from 'react';
import { setCallKeepAwake, setCallProximity } from './call-screen';
import type { CallPhase } from './types';

/**
 * The screen, for as long as a call lasts.
 *
 * ---------------------------------------------------------------------------
 * TWO RULES, AND THEY ARE NOT THE SAME RULE
 * ---------------------------------------------------------------------------
 * It is tempting to drive both off "is there a call", and that is wrong in
 * both directions:
 *
 *   PROXIMITY covers a narrower span than a call, because there are moments
 *   inside a call when the screen must be readable no matter what is near the
 *   sensor - see the two exclusions below.
 *
 *   KEEP AWAKE covers the whole call, including the ones proximity skips. A
 *   ringing handset that locks itself is a missed call.
 *
 * ---------------------------------------------------------------------------
 * WHERE THIS HOOK IS ALLOWED TO LIVE
 * ---------------------------------------------------------------------------
 * Exactly one place: `VoiceCallProvider`, which wraps the whole tree and is
 * mounted once. The native side holds one flag per process, so two live
 * instances of this hook would fight over it - one turning the lock on for its
 * call while the other turned it off for its own idle state, and the loser
 * would be whichever effect happened to run second.
 *
 * It deliberately does **not** live in either call overlay. Screen control
 * belongs to the state of the call, not to whether a particular component is
 * mounted: an overlay that unmounts while a call is still running would take
 * the lock down with it, or - worse, on a crash - leave it up.
 */

/**
 * Phases where the earpiece is the intended output.
 *
 * `incoming` is **out**, and it is the exclusion that matters most: the answer
 * screen is up and somebody has to be able to see Answer and Decline. Blank
 * that and an incoming call cannot be taken at all.
 *
 * `requesting-permission` is out for the same reason - the system microphone
 * dialog is on screen and has to be readable. Note that for the person
 * *answering*, that dialog is up while the call is already ringing, so this is
 * not the harmless edge it looks like.
 *
 * `ending` is in: the handset is usually still at an ear while the hang-up is
 * in flight, and letting the screen flash on for that half second is worse
 * than leaving it dark until the call is actually over.
 */
const PROXIMITY_PHASES: CallPhase[] = [
  'dialling',
  'ringing-out',
  'connecting',
  'connected',
  'ending',
];

/**
 * Phases where the screen must not lock itself.
 *
 * The proximity list plus the two it excludes. A handset that dims and locks
 * while its owner is reading an incoming call, or while a permission dialog is
 * waiting for a tap, is the same bug wearing a different hat.
 */
const KEEP_AWAKE_PHASES: CallPhase[] = [
  'requesting-permission',
  'incoming',
  ...PROXIMITY_PHASES,
];

/**
 * @param phase   where the call is.
 * @param speaker whether the loudspeaker is on. When it is, the handset is on
 *   a table or in a hand rather than at an ear, and a palm passing over the
 *   sensor must not black the screen out mid-sentence.
 */
export function useCallScreen(phase: CallPhase, speaker: boolean): void {
  const wantProximity = PROXIMITY_PHASES.includes(phase) && !speaker;
  const wantKeepAwake = KEEP_AWAKE_PHASES.includes(phase);

  useEffect(() => {
    void setCallProximity(wantProximity);
  }, [wantProximity]);

  useEffect(() => {
    void setCallKeepAwake(wantKeepAwake);
  }, [wantKeepAwake]);

  /*
   * Unmount is its own effect, with no dependencies, so it runs once when the
   * hook goes away rather than on every change above.
   *
   * This is the innermost of three guards against the one failure this feature
   * can produce that a person would call "my phone is broken": a proximity
   * lock left held, and a screen that will not come on. The native module
   * releases again on host destroy and once more when the React context is
   * invalidated - and if all three somehow miss, Android drops the lock when
   * the process dies.
   */
  useEffect(
    () => () => {
      void setCallProximity(false);
      void setCallKeepAwake(false);
    },
    [],
  );
}
